export const LINE_INTERVIEW_ESCALATION_FIELD = 'interview_escalation'

export const DEFAULT_LINE_INTERVIEW_SCALE_TASK_THRESHOLD = 4
export const DEFAULT_LINE_INTERVIEW_SCALE_CHECKLIST_THRESHOLD = 12
export const DEFAULT_LINE_INTERVIEW_ATTACHMENT_CONFLICT_RATIO = 2

export type LineInterviewEscalationTrigger =
  | { kind: 'scale'; taskCount: number; checklistCount: number }
  | { kind: 'conflict'; attachmentCount: number; promptCount: number }

export interface LineInterviewEscalation {
  triggers: LineInterviewEscalationTrigger[]
}

type DraftContainer = {
  checklist?: unknown
  tasks?: unknown
  subTasks?: unknown
}

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  dozen: 12,
}

const asRecord = (value: unknown): DraftContainer | null =>
  value && typeof value === 'object' ? value as DraftContainer : null

const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : []

/** Counts the model draft deterministically; this is the scale-sniff pass. */
export const countProjectedPlanScope = (draft: unknown) => {
  const root = asRecord(draft)
  if (!root) return { taskCount: 0, checklistCount: 0 }

  let taskCount = 0
  let checklistCount = 0
  const walk = (container: DraftContainer) => {
    checklistCount += asArray(container.checklist).length
    for (const subtask of asArray(container.subTasks)) {
      const child = asRecord(subtask)
      if (child) walk(child)
    }
  }

  checklistCount += asArray(root.checklist).length
  for (const task of asArray(root.tasks)) {
    const normalizedTask = asRecord(task)
    if (!normalizedTask) continue
    taskCount += 1
    walk(normalizedTask)
  }
  return { taskCount, checklistCount }
}

const explicitCountPattern = /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|dozen)\s+(?:goals?|tasks?|subtasks?|checklists?|items?|steps?)\b/gi

/** A stated count is a deliberate scope boundary, including casual small-count language. */
export const hasExplicitPromptScopeCount = (transcript: string) => {
  explicitCountPattern.lastIndex = 0
  const hasNumericCount = explicitCountPattern.test(transcript)
  explicitCountPattern.lastIndex = 0
  return hasNumericCount || /\b(?:just\s+)?(?:a\s+)?(?:few|couple|handful)\b/i.test(transcript)
}

/** Conservative prompt scope estimate used only for the attachment conflict ratio. */
export const estimatePromptScopeCount = (transcript: string) => {
  let count = 0
  for (const match of transcript.matchAll(new RegExp(explicitCountPattern))) {
    const token = match[1]?.toLowerCase() ?? ''
    count += /^\d+$/.test(token) ? Number.parseInt(token, 10) : (NUMBER_WORDS[token] ?? 0)
  }
  if (count > 0) return count
  if (/\b(?:just\s+)?(?:a\s+)?few\b/i.test(transcript)) return 3
  if (/\b(?:a\s+)?couple\b/i.test(transcript)) return 2
  return 1
}

export const findAttachmentInventoryExtractedCount = (turns: Array<{
  role: string
  questionSet?: { questions?: Array<{ type?: string; attachmentInventory?: { extractedCount?: number } }> }
}>) => {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const question = turns[index]?.questionSet?.questions?.find((candidate) => candidate.type === 'ATTACHMENT_INVENTORY')
    const count = question?.attachmentInventory?.extractedCount
    if (typeof count === 'number' && Number.isFinite(count) && count > 0) return Math.floor(count)
  }
  return 0
}

export const hasLineInterviewEscalationResponse = (turns: Array<{ role: string; text: string; requestText?: string }>) =>
  turns.some((turn) => turn.role === 'USER'
    && /\[LINE_CLARIFICATION_RESPONSE\][\s\S]*?field:\s*interview_escalation\b/i.test(turn.requestText ?? turn.text))

export const planLineInterviewEscalation = ({
  draft,
  transcript,
  attachmentExtractedCount,
  alreadyAnswered,
  taskThreshold = DEFAULT_LINE_INTERVIEW_SCALE_TASK_THRESHOLD,
  checklistThreshold = DEFAULT_LINE_INTERVIEW_SCALE_CHECKLIST_THRESHOLD,
  attachmentConflictRatio = DEFAULT_LINE_INTERVIEW_ATTACHMENT_CONFLICT_RATIO,
}: {
  draft: unknown
  transcript: string
  attachmentExtractedCount: number
  alreadyAnswered: boolean
  taskThreshold?: number
  checklistThreshold?: number
  attachmentConflictRatio?: number
}): LineInterviewEscalation | null => {
  if (alreadyAnswered) return null
  const { taskCount, checklistCount } = countProjectedPlanScope(draft)
  const triggers: LineInterviewEscalationTrigger[] = []

  if (!hasExplicitPromptScopeCount(transcript)
    && (taskCount > taskThreshold || checklistCount > checklistThreshold)) {
    triggers.push({ kind: 'scale', taskCount, checklistCount })
  }

  const promptCount = estimatePromptScopeCount(transcript)
  if (attachmentExtractedCount > attachmentConflictRatio * promptCount) {
    triggers.push({ kind: 'conflict', attachmentCount: attachmentExtractedCount, promptCount })
  }

  return triggers.length > 0 ? { triggers } : null
}
